import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaRegCopy, FaCheck, FaLock, FaPaperPlane, FaGlobeAfrica } from 'react-icons/fa';
import './Give.css';
import GivingSection from '../GivingSection';
import mtnImage from '../../Assets/mtn.jpeg';
import airtelImage from '../../Assets/airtel.jpeg';
import bankImage from '../../Assets/dfcu.jpeg';
import Footer from '../Footer';

/* The City Church — official giving details.
   "Give Now" routes to Church Center for secure online/card giving. */
const ONLINE_GIVING_URL = 'https://thecitychurchluzira.churchcenter.com/giving';

const METHODS = [
  {
    id: 'mtn',
    name: 'MTN Mobile Money',
    tag: 'Mobile Money',
    logo: mtnImage,
    rows: [
      { label: 'Dial', value: '*165*3#', copy: true },
      { label: 'Merchant code', value: '66216888', copy: true },
    ],
    note: 'Dial *165*3#, enter the merchant code above, then confirm with your MTN MoMo PIN.',
  },
  {
    id: 'airtel',
    name: 'Airtel Money',
    tag: 'Mobile Money',
    logo: airtelImage,
    rows: [
      { label: 'Dial', value: '*185*9#', copy: true },
      { label: 'Merchant code', value: '1203756', copy: true },
    ],
    note: 'Dial *185*9#, enter the merchant code above, then confirm with your Airtel Money PIN.',
  },
  {
    id: 'dfcu',
    name: 'DFCU Bank',
    tag: 'Bank Transfer',
    logo: bankImage,
    rows: [
      { label: 'Account name', value: 'The City Church Ltd', copy: true },
      { label: 'Account number', value: '01413659276174', copy: true },
    ],
    note: 'Transfer to the account above and use your name as the reference so we can thank you personally.',
  },
  {
    id: 'sendwave',
    name: 'Sendwave',
    tag: 'International',
    icon: <FaPaperPlane aria-hidden="true" />,
    rows: [
      { label: 'Send to', value: '+256792914565', copy: true },
    ],
    note: 'Giving from abroad? Send to the number above using the Sendwave app.',
  },
  {
    id: 'paysend',
    name: 'Paysend',
    tag: 'International',
    icon: <FaGlobeAfrica aria-hidden="true" />,
    rows: [
      { label: 'Send to', value: '+256792914565', copy: true },
    ],
    note: 'Prefer Paysend? Send internationally to the number above.',
  },
];

function Give() {
  const [copiedKey, setCopiedKey] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const hideTimer = useRef(null);

  const handleCopy = async (key, value) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        throw new Error('clipboard-unavailable');
      }
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* no-op */ }
      document.body.removeChild(ta);
    }

    setCopiedKey(key);
    setToastVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      setCopiedKey((k) => (k === key ? null : k));
      setToastVisible(false);
    }, 2000);
  };

  return (
    <>
      <GivingSection onlineGivingUrl={ONLINE_GIVING_URL} methodsId="give-methods" />

      <section className="tcc-section give-methods" id="give-methods" aria-labelledby="give-methods-heading">
        <div className="tcc-container">
          <div className="give-methods__head">
            <p className="tcc-eyebrow give-methods__eyebrow">Local &amp; International Giving</p>
            <h2 className="tcc-h2 give-methods__heading" id="give-methods-heading">
              Or Give Directly
            </h2>
            <p className="tcc-lead give-methods__lead">
              Choose a method below and tap copy&mdash;every code, account number and
              phone number is one tap away.
            </p>
          </div>

          <ul className="give-cards">
            {METHODS.map((m) => (
              <li className="give-card" key={m.id}>
                <div className="give-card__head">
                  <span className="give-card__brand">
                    {m.logo ? (
                      <img src={m.logo} alt={`${m.name} logo`} className="give-card__logo" />
                    ) : (
                      <span className="give-card__icon">{m.icon}</span>
                    )}
                  </span>
                  <div className="give-card__titles">
                    <h3 className="give-card__name">{m.name}</h3>
                    <span className="give-card__tag">{m.tag}</span>
                  </div>
                </div>

                <dl className="give-card__rows">
                  {m.rows.map((r) => {
                    const key = `${m.id}-${r.label}`;
                    return (
                      <div className="give-row" key={key}>
                        <dt className="give-row__label">{r.label}</dt>
                        <dd className="give-row__data">
                          <span className="give-row__value">{r.value}</span>
                          {r.copy && (
                            <button
                              type="button"
                              className={`give-copy${copiedKey === key ? ' is-copied' : ''}`}
                              onClick={() => handleCopy(key, r.value)}
                              aria-label={`Copy ${m.name} ${r.label.toLowerCase()}`}
                            >
                              {copiedKey === key ? (
                                <><FaCheck aria-hidden="true" /> Copied</>
                              ) : (
                                <><FaRegCopy aria-hidden="true" /> Copy</>
                              )}
                            </button>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>

                <p className="give-card__note">{m.note}</p>
              </li>
            ))}
          </ul>

          <p className="give-help">
            <FaLock aria-hidden="true" /> These are The City Church&rsquo;s verified giving
            details. Questions? <Link to="/contact-us">Contact our team</Link>.
          </p>
        </div>
      </section>

      <div
        className={`give-toast${toastVisible ? ' is-visible' : ''}`}
        role="status"
        aria-live="polite"
      >
        <FaCheck aria-hidden="true" /> Copied to clipboard
      </div>

      <Footer />
    </>
  );
}

export default Give;
